/**
 * AlertBar — bandeau d'alertes système (S6 PR5).
 *
 * Affiche les alertes courantes (tokens 30/15/7j, queue failed, polling
 * en panne, scheduler stale) ordonnées critical > warning > info. Vide
 * → la barre disparaît silencieusement (UX silencieuse quand tout va bien).
 *
 * Pas d'action inline (pour rester lisible) — chaque alerte propose
 * un texte explicite. Pour agir, l'utilisatrice navigue vers la
 * section concernée (Settings → Comptes connectés pour OAuth, etc.).
 */

import { AlertCircle, AlertTriangle, Info } from 'lucide-react'

import { useDashboardAlerts, type Alert } from '@/hooks/useDashboard'

import './AlertBar.css'

export default function AlertBar() {
  const query = useDashboardAlerts()

  if (query.isLoading || query.isError) {
    // Silencieux — pas de placeholder bruyant.
    return null
  }

  const alerts = query.data ?? []
  if (alerts.length === 0) {
    return null
  }

  return (
    <section className="alert-bar" aria-label="Alertes système">
      <ul className="alert-bar-list">
        {alerts.map((alert, idx) => (
          <AlertItem key={`${alert.kind}-${alert.entity_id ?? idx}`} alert={alert} />
        ))}
      </ul>
    </section>
  )
}

interface AlertItemProps {
  alert: Alert
}

function AlertItem({ alert }: AlertItemProps) {
  const Icon =
    alert.severity === 'critical'
      ? AlertCircle
      : alert.severity === 'warning'
        ? AlertTriangle
        : Info

  return (
    <li className={`alert-bar-item alert-bar-item--${alert.severity}`}>
      <Icon aria-hidden="true" size={18} className="alert-bar-icon" />
      <div className="alert-bar-content">
        <strong className="alert-bar-title">{alert.title}</strong>
        <p className="alert-bar-detail ui-body-sm">{alert.detail}</p>
      </div>
    </li>
  )
}
